import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Save, FileWarning, X, AlertCircle, ArrowLeft } from "lucide-react";
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";
const EditSupplier = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: "",
        mobile_number: "",
        email: "",
        address: "",
        gst_number: "",
        is_interstate: false,
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("success");

    // Fetch existing supplier details
    useEffect(() => {
        const fetchSupplier = async () => {
            try {
                setIsLoading(true);
                const response = await axiosInstance.get(`/supplier/${id}`);
                const { name, mobile_number, email, address, gst_number, is_interstate } = response.data;
                setFormData({ name, mobile_number, email, address, gst_number, is_interstate: is_interstate || false });
            } catch (error) {
                console.error("Error fetching supplier:", error);
                showNotification("Failed to fetch supplier data.", "error");
            } finally {
                setIsLoading(false);
            }
        };
        fetchSupplier();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({ ...prevData, [name]: value }));
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = "Name is required.";
        if (!formData.mobile_number) newErrors.mobile_number = "Mobile Number is required.";
        // if (!formData.email) newErrors.email = "Email Address is required.";
        // if (!formData.address) newErrors.address = "Address is required.";
        if (formData.gst_number && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gst_number)) {
            newErrors.gst_number = "Invalid GST number format.";
        }
        return newErrors;
    };

    const showNotification = (message, type = "success") => {
        setAlertMessage(message);
        setAlertType(type);
        setShowAlert(true);
        setTimeout(() => {
            setShowAlert(false);
        }, 5000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            showNotification("Please fill in all required fields.", "error");
            return;
        }
        setIsSubmitting(true);
        try {
            const response = await axiosInstance.put(`/supplier/${id}`, {
                name: formData.name,
                mobile_number: formData.mobile_number,
                email: formData.email,
                address: formData.address,
                gst_number: formData.gst_number,
                is_interstate: formData.is_interstate,
            });
            navigate("/supplier", {
                state: { successMessage: "Supplier updated successfully!" },
            });
        } catch (error) {
            console.error("Error updating supplier:", error);
            if (error.response?.data?.error === "This mobile number is already registered.") {
                setErrors(prev => ({ ...prev, mobile_number: "This mobile number is already registered." }));
                showNotification("This mobile number is already registered.", "error");
            } else {
                showNotification(error.response?.data?.message || "Failed to update supplier.", "error");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Loading skeleton UI
    if (isLoading) {
        return (
            <Authenticated>
                <div className="p-6 max-w-7xl mx-auto">
                    <div className="flex justify-between items-center mb-6">
                        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse"></div>
                        <div className="h-10 w-32 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {[...Array(4)].map((_, i) => (
                                <div key={i}>
                                    <div className="h-6 w-36 bg-gray-200 rounded animate-pulse mb-4"></div>
                                    <div className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Authenticated>
        );
    }

    return (
        <Authenticated>
            <div className="p-4 sm:p-6 max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center">
                            <button
                                onClick={() => navigate("/supplier")}
                                className="mr-2 text-gray-500 hover:text-teal-600 transition-colors"
                            >
                                <ArrowLeft className="w-[18px] h-[18px] sm:w-5 sm:h-5" />
                            </button>
                            Edit Supplier
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">Update supplier details for ID: {id}</p>
                    </div>
                </div>

                {/* Alert component */}
                {showAlert && (
                    <div
                        className={`mb-6 p-4 rounded-lg flex items-start justify-between ${alertType === "success"
                            ? "bg-green-50 text-green-800 border-l-4 border-green-500"
                            : "bg-red-50 text-red-800 border-l-4 border-red-500"
                            }`}
                    >
                        <div className="flex items-center">
                            {alertType === "success" ? (
                                <Save size={20} className="mr-3 text-green-500" />
                            ) : (
                                <FileWarning size={20} className="mr-3 text-red-500" />
                            )}
                            <span>{alertMessage}</span>
                        </div>
                        <button onClick={() => setShowAlert(false)} className="text-gray-500 hover:text-gray-700">
                            <X size={18} />
                        </button>
                    </div>
                )}

                {/* Form */}
                <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                            {/* Name Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Name <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-3 text-gray-400">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                                    </span>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Enter name"
                                        className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.name ? "border-red-500" : "border-gray-200"
                                            } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                    />
                                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                                </div>
                            </div>

                            {/* Mobile Number Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Mobile Number <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-3 text-gray-400">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12" y2="18"></line></svg>
                                    </span>
                                    <input
                                        type="text"
                                        name="mobile_number"
                                        value={formData.mobile_number}
                                        onChange={handleChange}
                                        placeholder="Enter mobile number"
                                        className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.mobile_number ? "border-red-500" : "border-gray-200"
                                            } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                    />
                                    {errors.mobile_number && <p className="text-red-500 text-xs mt-1">{errors.mobile_number}</p>}
                                </div>
                            </div>

                            {/* Email Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email Address
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-3 text-gray-400">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                                    </span>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="Enter email address"
                                        className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.email ? "border-red-500" : "border-gray-200"
                                            } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                    />
                                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                                </div>
                            </div>
                        </div>

                        {/* GST Number Field */}
                        <div className="mt-6">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                GST Number
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    name="gst_number"
                                    value={formData.gst_number}
                                    onChange={handleChange}
                                    placeholder="Enter GST number"
                                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.gst_number ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                />
                                <span className="absolute left-3 top-3 text-gray-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><path d="M22 6l-10 7L2 6"></path></svg>
                                </span>
                                {errors.gst_number && <p className="text-red-500 text-xs mt-1">{errors.gst_number}</p>}
                            </div>
                        </div>

                        {/* Interstate Switch */}
                        <div className="mt-6">
                            <label className="flex items-center space-x-3 cursor-pointer">
                                <div className="relative">
                                    <input
                                        type="checkbox"
                                        name="is_interstate"
                                        checked={formData.is_interstate}
                                        onChange={(e) => setFormData(prev => ({ ...prev, is_interstate: e.target.checked }))}
                                        className="sr-only"
                                    />
                                    <div className={`block ${formData.is_interstate ? 'bg-teal-600' : 'bg-gray-200'} w-14 h-8 rounded-full transition-colors duration-300 ease-in-out`}></div>
                                    <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform duration-300 ease-in-out ${formData.is_interstate ? 'transform translate-x-6' : ''}`}></div>
                                </div>
                                <span className="text-sm font-medium text-gray-700">Interstate Supplier</span>
                            </label>
                            <p className="text-xs text-gray-500 mt-1 ml-[4.5rem]">Toggle this if the supplier is from a different state</p>
                        </div>

                        {/* Address Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Address
                            </label>
                            <textarea
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder="Enter address"
                                rows="2"
                                className={`w-full px-3 py-2.5 bg-white border ${errors.address ? "border-red-500" : "border-gray-200"
                                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                            ></textarea>
                            {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                        </div>

                        {/* Bottom action bar */}
                        <div className="flex items-center text-gray-500 text-sm">
                            <AlertCircle size={16} className="mr-2" />
                            <span>
                                All fields marked with <span className="text-red-500">*</span> are required
                            </span>
                        </div>
                        <div className="flex flex-col-reverse sm:flex-row justify-between items-center mt-6 gap-4">
                            <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-3">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`w-full sm:w-auto px-6 py-2.5 rounded-lg flex items-center justify-center ${isSubmitting
                                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        : "bg-teal-600 text-white hover:bg-teal-700 shadow-md hover:shadow-lg transition-all duration-200"
                                        }`}
                                >
                                    <Save size={18} className="mr-2" />
                                    {isSubmitting ? "Updating..." : "Update Supplier"}
                                </button>
                                <button
                                    onClick={() => navigate("/supplier")}
                                    className="w-full sm:w-auto px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                    type="button"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </Authenticated>
    );
};

export default EditSupplier;